import { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import cn from 'classnames';
import moment from 'moment';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import './BrandCancellations.scss';

import BrandCancellationsPreviewTable from './BrandCancellationsPreviewTable';
import BrandCancellationsQueue from './BrandCancellationsQueue';
import { getBrandBasicObjects } from '../../api/brands';
import {
  getBrandCancellationPreview,
  getBrandCancellations,
  queueBrandCancellation,
  updateAutomaticBrandCancellation
} from '../../api/brand_cancellations';
import {
  CATEGORY_CONFIGS,
  OVERDUE_GRACE_MS,
  getDefaultCancellationFlags,
  getTodayDateStringET,
  formatTaskDateET,
  isCancellationCategoryEmpty
} from '../../helpers/brand_cancellation_helpers';
import { useInterval } from '../../hooks/useInterval';

const OFFER_CLEANUP_FLAGS = ['remove_codes', 'remove_brand_integration'];

const BrandCancellations = () => {
  const [brandOptions, setBrandOptions] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [flags, setFlags] = useState({});
  const [isScheduling, setIsScheduling] = useState(false);
  const [executionDate, setExecutionDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingAutomaticCancellation, setIsSavingAutomaticCancellation] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const isMountedRef = useRef(true);
  const previewRequestRef = useRef(0);
  const tasksRequestRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      previewRequestRef.current += 1;
    };
  }, []);

  const fetchTasks = useCallback(({ silent } = {}) => {
    const requestId = ++tasksRequestRef.current;
    if (!silent) setIsLoadingTasks(true);
    getBrandCancellations()
      .then(res => {
        // Ordering guard: a slow poll must not overwrite the response to a later cancel/queue refresh.
        if (isMountedRef.current && tasksRequestRef.current === requestId) setTasks(res.tasks || []);
      })
      .catch(err => {
        console.error('Failed to fetch brand cancellations:', err);
        if (!silent && isMountedRef.current) cogoToast.error('Could not load the cancellation queue.');
      })
      .finally(() => {
        if (!silent && isMountedRef.current) setIsLoadingTasks(false);
      });
  }, []);

  useEffect(() => {
    getBrandBasicObjects()
      .then(brands => {
        if (isMountedRef.current) setBrandOptions(brands.map(brand => ({ value: brand.id, label: `${brand.name} (${brand.id})` })));
      })
      .catch(err => {
        console.error('Failed to load brands:', err);
        if (isMountedRef.current) cogoToast.error('Could not load the brand list.');
      });
    fetchTasks();
  }, [fetchTasks]);

  const now = Date.now();
  const hasProcessingTasks = tasks.some(task => task.status === 'PROCESSING');
  const pendingWithin = windowMs =>
    tasks.some(task => task.status === 'PENDING' && task.executionTimestamp && task.executionTimestamp * 1000 < now + windowMs);
  const hasImminentTasks =
    hasProcessingTasks ||
    tasks.some(
      task =>
        task.status === 'PENDING' &&
        task.executionTimestamp &&
        task.executionTimestamp * 1000 < now + 60 * 1000 &&
        task.executionTimestamp * 1000 > now - OVERDUE_GRACE_MS
    );
  // 5s while something is running or about to; 60s while a run is due within the hour
  // (or stalled past due); nothing for far-future schedules — Refresh covers those.
  const pollDelay = hasImminentTasks ? 5000 : pendingWithin(60 * 60 * 1000) ? 60000 : null;

  useInterval(() => fetchTasks({ silent: true }), pollDelay);

  const openBrand = brandOption => {
    // Stale responses are dropped so a slow preview for a previously selected brand
    // can never overwrite the brand the admin is about to cancel.
    const requestId = ++previewRequestRef.current;
    setSelectedBrand(brandOption);
    setPreview(null);
    setFlags({});
    setIsScheduling(false);
    setExecutionDate('');
    if (!brandOption) {
      setIsLoadingPreview(false);
      return;
    }

    setIsLoadingPreview(true);
    getBrandCancellationPreview(brandOption.value)
      .then(res => {
        if (previewRequestRef.current !== requestId) return;
        const previewData = {
          brand: res.brand,
          allDomains: res.allDomains,
          defaultFlags: res.defaultFlags,
          automaticCancellation: res.automaticCancellation,
          categories: res.categories
        };
        setPreview(previewData);
        setFlags(getDefaultCancellationFlags(previewData));
      })
      .catch(err => {
        if (previewRequestRef.current !== requestId) return;
        cogoToast.error(err?.response?.data?.error || 'Could not load that brand.');
        setSelectedBrand(null);
      })
      .finally(() => {
        if (previewRequestRef.current !== requestId) return;
        setIsLoadingPreview(false);
      });
  };

  const toggleFlag = flag =>
    setFlags(prev => {
      const next = { ...prev, [flag]: !prev[flag] };
      // Keeping the commission rate keeps the network lock with it (ENG-2658), so the
      // lock follows the rates checkbox in both directions.
      if (flag === 'remove_rates') next.remove_locks = next.remove_rates;
      return next;
    });

  const saveAutomaticCancellation = keepCommissionRateAfterCancellation => {
    const requestId = previewRequestRef.current;
    setIsSavingAutomaticCancellation(true);
    updateAutomaticBrandCancellation(preview.brand.id, { keepCommissionRateAfterCancellation })
      .then(res => {
        if (!isMountedRef.current || previewRequestRef.current !== requestId) return;
        setPreview(prev => ({ ...prev, automaticCancellation: res.automaticCancellation }));
        cogoToast.success(
          keepCommissionRateAfterCancellation ? 'Commissions stay on when this brand cancels.' : 'Commissions come off when this brand cancels.'
        );
      })
      .catch(err => {
        if (isMountedRef.current) cogoToast.error(err?.response?.data?.error || 'Could not save that.');
      })
      .finally(() => {
        if (isMountedRef.current) setIsSavingAutomaticCancellation(false);
      });
  };

  const isCategoryEmpty = config => isCancellationCategoryEmpty(preview, config.flag);

  const selectAllWithData = () =>
    setFlags(Object.fromEntries(CATEGORY_CONFIGS.filter(config => !isCategoryEmpty(config)).map(config => [config.flag, true])));

  const checkedConfigs = CATEGORY_CONFIGS.filter(config => flags[config.flag]);
  const automaticCancellation = preview?.automaticCancellation;
  const automaticRemovals = CATEGORY_CONFIGS.filter(config => automaticCancellation?.flags?.includes(config.flag))
    .map(config => config.shortLabel)
    .join(', ');
  const hasOfferCleanup = OFFER_CLEANUP_FLAGS.some(flag => flags[flag]);
  const minScheduleDate = moment(getTodayDateStringET(), 'YYYY-MM-DD')
    .add(1, 'day')
    .format('YYYY-MM-DD');

  const submit = () => {
    setIsSubmitting(true);
    queueBrandCancellation({ brandId: preview.brand.id, ...flags, ...(isScheduling ? { executionDate } : {}) })
      .then(() => {
        if (!isMountedRef.current) return;
        cogoToast.success(isScheduling ? `Scheduled the cancellation for ${executionDate} (ET).` : 'Queued for the next worker pass.');
        openBrand(null);
        fetchTasks();
      })
      .catch(err => {
        if (isMountedRef.current) cogoToast.error(err?.response?.data?.error || 'Could not queue the cancellation.');
      })
      .finally(() => {
        if (isMountedRef.current) setIsSubmitting(false);
      });
  };

  const confirmSubmit = () => {
    if (isScheduling && !executionDate) return cogoToast.warn('Pick a date, or uncheck scheduling to run now.');

    const timing = isScheduling ? `It runs at midnight Eastern on ${executionDate}.` : 'It runs within seconds of queueing.';
    return confirmAlert({
      title: `Cancel ${preview.brand.name}?`,
      message: `This removes: ${checkedConfigs.map(config => config.shortLabel).join(', ')}. ${timing}`,
      buttons: [
        { label: 'Back', onClick: () => {}, className: 'cancel' },
        { label: isScheduling ? 'Schedule it' : 'Queue it now', onClick: submit }
      ]
    });
  };

  return (
    <div className='brand-cancellations-outer'>
      <main className='brand-cancellations-main'>
        <div className='brand-cancellations-top'>
          <div>
            <div className='brand-cancellations-title'>Brand Cancellations</div>
            <div className='brand-cancellations-rule'>
              Preview what exists, choose what to remove, run now or schedule — execution happens on the worker queue.
            </div>
          </div>
          <Select
            className='brand-cancellations-search'
            options={brandOptions}
            value={selectedBrand}
            onChange={openBrand}
            placeholder='Find a brand…'
            isClearable
          />
        </div>

        {!selectedBrand && !isLoadingPreview && (
          <div className='brand-cancellations-quiet'>Pick a brand to preview what a cancellation would remove.</div>
        )}
        {isLoadingPreview && <div className='brand-cancellations-quiet'>Loading preview…</div>}

        {!!preview && (
          <>
            <div className='brand-cancellations-brand-card'>
              <div className='brand-cancellations-brand-name'>
                {preview.brand.name} <span>#{preview.brand.id}</span>
              </div>
              <div className='brand-cancellations-brand-domains'>{(preview.allDomains || []).join(' · ')}</div>
            </div>

            {!!automaticCancellation && (
              <div className='brand-cancellations-automatic'>
                <div className='brand-cancellations-automatic-title'>Automatic cancellation</div>
                <div className='brand-cancellations-automatic-rule'>
                  When this brand's Stripe subscription ends, a cancellation is queued automatically and runs {automaticCancellation.graceHours} hours
                  later. It removes: {automaticRemovals}.
                </div>
                <label className='brand-cancellations-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={!!automaticCancellation.keepCommissionRateAfterCancellation}
                    disabled={isSavingAutomaticCancellation}
                    onChange={e => saveAutomaticCancellation(e.target.checked)}
                  />
                  <span>Keep commissions on after cancellation</span>
                </label>
                <div className='brand-cancellations-automatic-effect'>
                  The commission rate and its network lock stay in place; the other defaults still run. Saved for this brand as soon as it is checked.
                </div>
              </div>
            )}

            <button type='button' className='brand-cancellations-select-all-btn' onClick={selectAllWithData}>
              Select everything with data
            </button>

            {CATEGORY_CONFIGS.map(config => {
              const category = preview.categories[config.flag];
              const isEmpty = isCategoryEmpty(config);
              const isLockHeldByRates =
                config.flag === 'remove_locks' && !flags.remove_rates && !isCancellationCategoryEmpty(preview, 'remove_rates');
              const isDefaultOff = preview.defaultFlags && !preview.defaultFlags.includes(config.flag);
              return (
                <div
                  key={config.flag}
                  className={cn('brand-cancellations-category', { 'brand-cancellations-category-empty': isEmpty && !category.retained?.count })}
                >
                  <label className='brand-cancellations-category-header'>
                    {/* Empty categories stay toggleable — a scheduled run can meet data that appears before its date. */}
                    <input type='checkbox' checked={!!flags[config.flag]} disabled={isLockHeldByRates} onChange={() => toggleFlag(config.flag)} />
                    <span className='brand-cancellations-category-label'>{config.label}</span>
                    <span className='brand-cancellations-category-count'>{category.count}</span>
                  </label>
                  <div className='brand-cancellations-category-effect'>
                    {config.effect}
                    {isDefaultOff && ' Not removed by default.'}
                  </div>
                  {config.flag === 'remove_rates' && (
                    <div className='brand-cancellations-category-effect'>Current brand commission rate: {preview.brand.commission_rate}%</div>
                  )}
                  {isLockHeldByRates && (
                    <div className='brand-cancellations-category-effect'>Kept automatically while the commission rate is kept.</div>
                  )}
                  {!!category.retained?.count && (
                    <div className='brand-cancellations-category-effect'>
                      <div>
                        {category.retained.count} Shopify connection{category.retained.count === 1 ? '' : 's'} retained for outstanding private offer
                        claims. Catalog syncing stops on these connections even when code and integration removal are unchecked. Removal dates are based on current claims; codes without an expiration keep their connection until
                        redeemed or explicitly revoked.
                      </div>
                      <BrandCancellationsPreviewTable
                        table={{
                          ...category.retained,
                          rows: category.retained.rows.map(connection => ({
                            ...connection,
                            removableAfter: connection.noExpiryClaims
                              ? 'No scheduled removal date'
                              : formatTaskDateET(connection.latestClaimExpiresAt)
                          }))
                        }}
                        columns={['domain', 'outstandingClaims', 'noExpiryClaims', 'removableAfter']}
                      />
                    </div>
                  )}
                  {config.tables ? (
                    config.tables.map(({ key, columns }) => <BrandCancellationsPreviewTable key={key} table={category[key]} columns={columns} />)
                  ) : (
                    <BrandCancellationsPreviewTable table={category} columns={config.columns} />
                  )}
                </div>
              );
            })}

            {hasOfferCleanup && (
              <div className='brand-cancellations-warning'>
                Removing codes or integrations ends public sales and stops new private offer claims. Already-claimed codes, including claims still
                being issued, keep their original terms and expiration. Ending offers is not rolled back if the cancellation later fails.
              </div>
            )}

            <div className='brand-cancellations-timing'>
              <label className='brand-cancellations-checkbox-label'>
                <input
                  type='checkbox'
                  checked={isScheduling}
                  onChange={e => {
                    setIsScheduling(e.target.checked);
                    setExecutionDate('');
                  }}
                />
                <span>Schedule for a future date instead of running now</span>
              </label>
              {isScheduling && (
                <label className='brand-cancellations-date-label'>
                  Runs at midnight Eastern on
                  <input type='date' value={executionDate} min={minScheduleDate} onChange={e => setExecutionDate(e.target.value)} />
                </label>
              )}
            </div>

            <button
              type='button'
              className='brand-cancellations-submit-btn'
              disabled={isSubmitting || checkedConfigs.length === 0}
              onClick={confirmSubmit}
            >
              {isSubmitting ? 'Queueing…' : isScheduling ? 'Schedule cancellation' : 'Queue cancellation'}
            </button>
          </>
        )}
      </main>

      <BrandCancellationsQueue tasks={tasks} isLoading={isLoadingTasks} onRefresh={() => fetchTasks()} onTasksChanged={fetchTasks} />
    </div>
  );
};

export default BrandCancellations;
